/**
 * Plant Loop Editor Page
 *
 * Page component for the visual plant loop editor.
 * Provides controls for creating, managing, and editing plant loops.
 */

import { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  usePlantLoopStore,
  PlantLoopEditor,
  LOOP_TYPE_NAMES,
  LOOP_COLORS,
  detectPlantLoops,
  generateFlatPlantGraph,
  getPlantDetectionStats,
} from '@/plant';
import type { PlantLoopType } from '@/plant';
import { useIDFStore } from '@/stores/idfStore';
import { useIsMobile } from '@/hooks';
import { FileRequiredEmptyState } from '@/components/FileRequiredEmptyState';

/**
 * Loop type selector dropdown
 */
function LoopTypeSelector({
  onSelect,
  onClose,
}: {
  onSelect: (type: PlantLoopType) => void;
  onClose: () => void;
}) {
  const loopTypes: PlantLoopType[] = [
    'chilled-water',
    'hot-water',
    'condenser-water',
    'ground-loop',
    'service-hot-water',
  ];

  return (
    <div className="absolute left-0 top-full z-50 mt-1 w-56 md:w-64 rounded-md border bg-popover shadow-lg">
      <div className="py-1">
        {loopTypes.map((type) => (
          <button
            key={type}
            onClick={() => {
              onSelect(type);
              onClose();
            }}
            className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-muted"
          >
            <div
              className="h-4 w-4 rounded-full flex-shrink-0"
              style={{ backgroundColor: LOOP_COLORS[type].primary }}
            />
            <span className="text-sm text-foreground">{LOOP_TYPE_NAMES[type]}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Loop tabs component
 */
function LoopTabs() {
  const loops = usePlantLoopStore((state) => state.loops);
  const activeLoopId = usePlantLoopStore((state) => state.activeLoopId);
  const setActiveLoop = usePlantLoopStore((state) => state.setActiveLoop);
  const deleteLoop = usePlantLoopStore((state) => state.deleteLoop);

  if (loops.length === 0) return null;

  return (
    <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin" data-onboarding="plant-loop-tabs">
      {loops.map((loop) => (
        <div
          key={loop.id}
          className={`
            group flex items-center gap-2 rounded-t-md border-b-2 px-2 md:px-3 py-1.5 text-sm flex-shrink-0
            ${activeLoopId === loop.id
              ? 'border-blue-500 bg-background text-foreground'
              : 'border-transparent bg-muted text-muted-foreground hover:bg-muted/80'
            }
          `}
        >
          <button
            onClick={() => { setActiveLoop(loop.id); }}
            className="flex items-center gap-2"
          >
            <div
              className="h-3 w-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: LOOP_COLORS[loop.config.type].primary }}
            />
            <span className="max-w-[100px] md:max-w-[150px] truncate">{loop.config.name}</span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Delete "${loop.config.name}"?`)) {
                deleteLoop(loop.id);
              }
            }}
            className="ml-1 rounded p-0.5 opacity-0 hover:bg-muted group-hover:opacity-100"
            title="Delete loop"
          >
            <svg className="h-3 w-3 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}

export function PlantLoopEditorPage() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [showLoopTypeMenu, setShowLoopTypeMenu] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectionMessage, setDetectionMessage] = useState<string | null>(null);

  // Track timeout for cleanup to prevent memory leaks
  const messageTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
    };
  }, []);

  // Store state and actions
  const loops = usePlantLoopStore((state) => state.loops);
  const createLoop = usePlantLoopStore((state) => state.createLoop);
  const getActiveLoop = usePlantLoopStore((state) => state.getActiveLoop);
  const updateLoopConfig = usePlantLoopStore((state) => state.updateLoopConfig);
  const isDirty = usePlantLoopStore((state) => state.isDirty);
  const validateActiveLoop = usePlantLoopStore((state) => state.validateActiveLoop);
  const clear = usePlantLoopStore((state) => state.clear);
  const setNodes = usePlantLoopStore((state) => state.setNodes);
  const setEdges = usePlantLoopStore((state) => state.setEdges);
  const setActiveLoop = usePlantLoopStore((state) => state.setActiveLoop);

  // IDF Store
  const idfDocument = useIDFStore((state) => state.document);

  const activeLoop = getActiveLoop();

  // Auto-detect plant loops when IDF is loaded and no loops exist
  useEffect(() => {
    // Only auto-detect if:
    // 1. IDF document exists
    // 2. No existing loops
    // 3. Not already detecting
    if (!idfDocument || loops.length > 0 || isDetecting) {
      return;
    }

    // Small delay to ensure page is mounted
    const timer = setTimeout(() => {
      handleAutoDetect();
    }, 100);

    return () => { clearTimeout(timer); };
  }, [idfDocument]); // Only trigger when idfDocument changes, not on every render

  /**
   * Handle creating a new loop
   */
  const handleCreateLoop = useCallback(
    (type: PlantLoopType) => {
      createLoop(type);
    },
    [createLoop]
  );

  /**
   * Handle new project
   */
  const handleNew = useCallback(() => {
    if (isDirty) {
      const confirm = window.confirm(
        'You have unsaved changes. Are you sure you want to create a new project?'
      );
      if (!confirm) return;
    }
    clear();
  }, [isDirty, clear]);

  /**
   * Handle validation
   */
  const handleValidate = useCallback(() => {
    const result = validateActiveLoop();
    if (result.valid) {
      alert('Loop is valid!');
    } else {
      const messages = [
        ...result.errors.map((e) => `Error: ${e.message}`),
        ...result.warnings.map((w) => `Warning: ${w.message}`),
      ];
      alert(messages.join('\n'));
    }
  }, [validateActiveLoop]);

  /**
   * Export loop configuration as JSON
   */
  const handleExportJSON = useCallback(() => {
    const data = {
      loops: loops.map((loop) => ({
        ...loop,
      })),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plant-loops.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [loops]);

  // Helper to set message with auto-clear timeout
  const setMessageWithTimeout = useCallback((message: string | null, duration: number) => {
    // Clear any existing timeout
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
      messageTimeoutRef.current = null;
    }
    setDetectionMessage(message);
    if (message !== null) {
      messageTimeoutRef.current = setTimeout(() => {
        setDetectionMessage(null);
        messageTimeoutRef.current = null;
      }, duration);
    }
  }, []);

  /**
   * Auto-detect plant loops from IDF
   */
  const handleAutoDetect = useCallback(() => {
    if (!idfDocument) {
      setMessageWithTimeout('No IDF document loaded. Please open an IDF file first.', 3000);
      return;
    }

    if (loops.length > 0) {
      const confirm = window.confirm(
        'This will replace the current plant loops with detected systems. Continue?'
      );
      if (!confirm) return;
    }

    setIsDetecting(true);
    setDetectionMessage('Detecting plant loops...');

    try {
      // Detect plant loops from IDF
      const result = detectPlantLoops(idfDocument.objects);
      const stats = getPlantDetectionStats(result);

      if (result.plantLoops.length === 0 && result.standaloneComponents.length === 0) {
        setMessageWithTimeout('No plant loops found in the IDF document.', 3000);
        setIsDetecting(false);
        return;
      }

      // Generate visual graph and loop configs
      const graph = generateFlatPlantGraph(result);

      // Clear current loops and load detected systems
      clear();

      // Add detected loops to the store
      // We need to add loops manually since generateFlatPlantGraph returns PlantLoop configs
      for (const loopConfig of graph.loops) {
        // Create loop in store
        const loopId = createLoop(loopConfig.config.type, loopConfig.config.name);

        // Get the loop and update its config
        const storeLoops = usePlantLoopStore.getState().loops;
        const newLoop = storeLoops.find(l => l.id === loopId);
        if (newLoop) {
          updateLoopConfig(loopId, {
            fluidType: loopConfig.config.fluidType,
            designExitTemp: loopConfig.config.designExitTemp,
            designDeltaT: loopConfig.config.designDeltaT,
          });
        }
      }

      // If we have loops, set the first one as active and set its nodes/edges
      if (graph.loops.length > 0) {
        const storeLoops = usePlantLoopStore.getState().loops;
        if (storeLoops.length > 0 && storeLoops[0]) {
          setActiveLoop(storeLoops[0].id);
          setNodes(graph.nodes);
          setEdges(graph.edges);
        }
      }

      // Show success message
      const message = `Detected ${stats.loopCount} plant loop(s), ${stats.supplyComponentCount + stats.demandComponentCount} components, ${stats.connectionCount} connections`;
      setMessageWithTimeout(message, 5000);

      // Show warnings if any
      if (result.warnings.length > 0) {
        console.warn('Detection warnings:', result.warnings);
      }
    } catch (e) {
      const error = e instanceof Error ? e.message : String(e);
      setMessageWithTimeout(`Detection failed: ${error}`, 5000);
    } finally {
      setIsDetecting(false);
    }
  }, [idfDocument, loops.length, clear, createLoop, updateLoopConfig, setActiveLoop, setNodes, setEdges, setMessageWithTimeout]);

  return (
    <div className={`flex flex-col ${isMobile ? 'h-full' : 'h-[calc(100dvh-4rem)]'}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 md:gap-4 border-b bg-card px-2 md:px-4 py-2">
        <div className="flex items-center gap-2">
          {!isMobile && (
            <button
              onClick={handleNew}
              className="rounded-md bg-secondary px-3 py-1.5 text-sm font-medium hover:bg-secondary/80"
            >
              New
            </button>
          )}

          {/* Create Loop dropdown */}
          <div className="relative">
            <button
              onClick={() => { setShowLoopTypeMenu(!showLoopTypeMenu); }}
              className="flex items-center gap-1 rounded-md bg-primary px-2 md:px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
              </svg>
              <span className="hidden sm:inline">Add Loop</span>
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </button>
            {showLoopTypeMenu && (
              <LoopTypeSelector
                onSelect={handleCreateLoop}
                onClose={() => { setShowLoopTypeMenu(false); }}
              />
            )}
          </div>

          {!isMobile && (
            <button
              onClick={handleExportJSON}
              disabled={loops.length === 0}
              className="rounded-md bg-secondary px-3 py-1.5 text-sm font-medium hover:bg-secondary/80 disabled:opacity-50"
            >
              Export JSON
            </button>
          )}
        </div>

        <div className="hidden md:block h-6 w-px bg-border" />

        {/* Auto-Detect */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleAutoDetect}
            disabled={!idfDocument || isDetecting}
            className="flex items-center gap-1 rounded-md bg-blue-600 px-2 md:px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            title="Auto-detect plant loops from loaded IDF"
          >
            {isDetecting ? (
              <>
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span className="hidden sm:inline">Detecting...</span>
              </>
            ) : (
              <>
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="hidden sm:inline">Auto-Detect</span>
              </>
            )}
          </button>
        </div>

        {/* Loop name editor - hidden on mobile */}
        {activeLoop && !isMobile && (
          <>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <label htmlFor="loop-name" className="text-sm text-muted-foreground">
                Loop:
              </label>
              <input
                id="loop-name"
                type="text"
                value={activeLoop.config.name}
                onChange={(e) => {
                  updateLoopConfig(activeLoop.id, { name: e.target.value });
                }}
                className="rounded-md border bg-background px-2 py-1 text-sm"
                placeholder="Loop name"
              />
            </div>
          </>
        )}

        {!isMobile && (
          <>
            <div className="h-6 w-px bg-border" />
            {/* Validation */}
            <button
              onClick={handleValidate}
              disabled={!activeLoop}
              className="rounded-md bg-secondary px-3 py-1.5 text-sm font-medium hover:bg-secondary/80 disabled:opacity-50"
              title="Validate loop configuration"
            >
              Validate
            </button>
          </>
        )}

        <div className="flex-1" />

        {/* Status */}
        <div className="flex items-center gap-2 md:gap-3 text-sm text-muted-foreground">
          <span>{loops.length} loop{loops.length !== 1 ? 's' : ''}</span>
          {isDirty && <span className="text-yellow-600">*</span>}
        </div>
      </div>

      {/* Detection Message */}
      {detectionMessage && (
        <div className={`border-b px-2 md:px-4 py-2 text-sm ${
          detectionMessage.includes('failed') || detectionMessage.includes('No ')
            ? 'bg-destructive/10 text-destructive border-destructive/20'
            : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800'
        }`}>
          {detectionMessage}
        </div>
      )}

      {/* Loop Tabs */}
      <div className="border-b bg-muted/50 px-2 md:px-4">
        <LoopTabs />
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-hidden">
        {!idfDocument && loops.length === 0 ? (
          <FileRequiredEmptyState
            reason="no-file"
            title="No Model Loaded"
            message="Load an IDF file to automatically detect plant loops, or start designing from scratch."
            action={{
              label: 'Go to Editor',
              onClick: () => navigate('/'),
            }}
          />
        ) : (
          <PlantLoopEditor height="100%" />
        )}
      </div>
    </div>
  );
}
